import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Orders } from 'src/app/models/Orders.model';
import { Products } from 'src/app/models/Product.model';
import { Documents } from 'src/app/models/documents.models';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * OrdiniDaFirmareDetaggliComponent — Diakonix modal opened from the
 * ordini-da-firmare list (Chunk D.2, Phase 1.5 v2.7.0).
 *
 * Shows readonly order info + 3 collapsible sections:
 *   • Prodotti   — products in the order
 *   • Documenti  — DDT / certificates / other docs
 *   • Etichette  — PDF links (Borderò, Etichette colli, Corriere, DDT)
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up:
 *   • dropped 4 kendo-dialog sizing leftovers (dialogWidth/Height,
 *     maxlength, minWidth),
 *   • dropped unused state: orders, dataSaved, orderNew, productsNew,
 *     orderToSend, clickedProduct, pr_code, clickLabel, ettArray,
 *     box, product, formBuilder usage (form is no longer wired in the
 *     new template — the info card is plain dd/dt readonly),
 *   • dropped DropdownService injection (priorities were loaded but
 *     never displayed),
 *   • dropped `triggerFunction` indirection (PDFs now go through
 *     direct `<a target="_blank">` in the template, no JS hop),
 *   • dropped `printBordero`, `printCarrier`, `printDDT`, `printAllLabels`
 *     methods — replaced by the `<a>` tags. Only `loadOrder` /
 *     `loadDocuments` / `loadAllBoxLabelsLink` remain as data fetches.
 *   • dropped ~5 console.log,
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: loadOrder by order_id, products extraction,
 * documents loading, PDF link generation, close-flow via
 * closeServ.daFirmareOff().
 */
@Component({
  selector: 'app-ordini-da-firmare-detaggli',
  templateUrl: './ordini-da-firmare-detaggli.component.html',
  styleUrls: ['./ordini-da-firmare-detaggli.component.scss'],
  standalone: false,
})


export class OrdiniDaFirmareDetaggliComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  @Input() openedFIRMARE = false;
  @Input() order_id = '-1';

  // ── Data ────────────────────────────────────────────────────────
  public order: Orders.IOrder | null = null;
  public documents: Documents.Document[] = [];

  // ── PDF links (surfaced in the Etichette section) ──────────────
  public linkBordero: string | null = null;
  public linkAllLabels: string | null = null;
  public linkCarrier: string | null = null;
  public linkToDDT: string | null = null;
  public isLinkToDDT = false;

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public formattedDate = '—';

  /** Single-open accordion section name. */
  public openSection: 'products' | 'documents' | 'labels' | null = 'products';

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadOrder();
  }

  private loadOrder(): void {
    this.loading = true;
    this.ordersServ
      .getOrder(this.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orderData: IWsResult) => {
          if (!orderData?.data) {
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.order = new Orders.Order(orderData.data);

          // 🚨 Fix 2026-07-08: `or_url_box_label` è wrong (path lowercase
          // malformato, 404 in prod). Fix con join via getNewBorderos +
          // order_number. Stesso pattern applicato in
          // storico-ordini-dettagli.
          this.linkBordero = null;
          this.resolveBorderoLink();

          this.linkCarrier =
            this.order?.shipment?.os_label_url?.[0]?.sm_label_link ?? null;
          this.isLinkToDDT = !!this.order.or_url_ddt?.includes('.pdf');
          this.linkToDDT = this.isLinkToDDT ? this.order.or_url_ddt : null;

          if (this.order.or_order_date) {
            this.formattedDate = formatDate(
              this.order.or_order_date,
              'dd/MM/yyyy',
              'it',
            );
          }

          this.loadDocuments();
          this.loadAllBoxLabelsLink();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Risolve il link PDF del borderò via `getNewBorderos` + join per
   * `order_number`. Fix 2026-07-08 per il 404 causato da
   * `or_url_box_label` malformato lato BE. Vedi commento dettagliato
   * in `storico-ordini-dettagli.component.ts`.
   */
  private resolveBorderoLink(): void {
    if (!this.order?.or_order_number) return;
    const targetOrderNum = +this.order.or_order_number;
    this.ordersServ
      .getNewBorderos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          const borderos = (result?.data ?? []) as Orders.OrderBorderos[];
          const match = borderos.find((b) => +b.order_number === targetOrderNum);
          this.linkBordero = match?.bo_pdf_link ?? null;
          this.cdr.detectChanges();
        },
        error: () => { /* link resta null — template gestisce già */ },
      });
  }

  private loadDocuments(): void {
    this.ordersServ
      .getDocuments(+this.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.documents = result ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  private loadAllBoxLabelsLink(): void {
    if (!this.order?.order_id || !this.order?.boxes?.length) return;
    this.ordersServ
      .getAllBoxLabels(
        this.order.order_id,
        this.order.boxes.map((_, i) => i + 1),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result) return;
          this.linkAllLabels = result.box_label_link_pdf ?? null;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Section toggle.
  // ──────────────────────────────────────────────────────────────────

  toggleSection(name: 'products' | 'documents' | 'labels'): void {
    this.openSection = this.openSection === name ? null : name;
  }

  isOpen(name: 'products' | 'documents' | 'labels'): boolean {
    return this.openSection === name;
  }

  /** Convenience getter for the template: do we have at least one PDF? */
  get hasAnyPdf(): boolean {
    return !!(this.linkBordero || this.linkAllLabels || this.linkCarrier || this.linkToDDT);
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.daFirmareOff();
  }
  
}
